// Authentication API service
import axios from 'axios';
import { baseClient } from './baseClient';

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
}

export interface User {
  id: number;
  email: string;
  is_admin: boolean;
  created_at: string;
}

export class AuthApi {
  private static instance: AuthApi;

  private constructor() { }

  public static getInstance(): AuthApi {
    if (!AuthApi.instance) {
      AuthApi.instance = new AuthApi();
    }
    return AuthApi.instance;
  }

  /**
   * Login with email and password
   */
  async login(email: string, password: string): Promise<LoginResponse> {
    // Clear any existing token before login
    const currentToken = baseClient.getToken();
    baseClient.setToken(null);

    try {
      console.log('🔐 Login attempt:', { email, url: '/auth/token' });

      // Use axios directly with full URL for auth endpoint
      const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';
      const authUrl = API_BASE_URL + '/auth/token';

      // FastAPI expects URL-encoded form data for token endpoint
      console.log('Sending login request to:', authUrl);
      const formData = new URLSearchParams();
      formData.append('username', email);
      formData.append('password', password);
      const axiosResponse = await axios.post<LoginResponse>(authUrl, formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        timeout: 30000,
      });
      const response = axiosResponse.data;
      console.log('✅ Login response received');

      // Store the new token
      if (response.access_token) {
        baseClient.setToken(response.access_token);
      }

      return response;
    } catch (error: any) {
      // Restore the previous token if login failed
      baseClient.setToken(currentToken);
      throw error;
    }
  }

  /**
   * Logout and clear token
   */
  logout(): void {
    baseClient.setToken(null);
  }

  /**
   * Get current user information
   */
  async getCurrentUser(): Promise<User> {
    return baseClient.get<User>('/user-profiles/profiles/me');
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return baseClient.getToken() !== null;
  }

  /**
   * Get stored token
   */
  getToken(): string | null {
    return baseClient.getToken();
  }

  /**
   * Set token manually (useful for token refresh)
   */
  setToken(token: string): void {
    baseClient.setToken(token);
  }
}

// Export singleton instance
export const authApi = AuthApi.getInstance(); 